import React from "react";
import { ChevronRight } from "lucide-react";
import pandaImg from "../../../../../assets/panda_main.png";

const DelegationFramework = ({ userId, subsectionId, onPreviousStep, onNextStep }) => {
  console.log("UserId for DelegationFramework:", userId, "SubsectionId:", subsectionId);

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-[1400px] mx-auto px-6">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20 items-start">
          {/* LEFT: Panda + description (narrow column) */}
          <div className="lg:col-span-1 flex flex-col gap-8">
            <div className="flex justify-center lg:justify-start">
              <img src={pandaImg} alt="Panda" className="w-60 max-w-full h-auto" />
            </div>

            <div>
              <h1 className="sr-only">Delegation Framework</h1>
              <p className="text-[#323232] font-normal font-gotham text-[15px] 2xl:text-[16px] leading-relaxed mb-4">
                You now have the opportunity to manage who will do these tasks. That will plan your career.
              </p>

              <div className="text-sm space-y-3">
                <div>
                  <span className="font-bold text-[#323232] font-gotham text-[15px] 2xl:text-[16px]">X Axis:</span>
                  <span className="text-[#323232] font-normal font-gotham text-[15px] 2xl:text-[16px] ml-2">Who could do this work?</span>
                </div>
                <div>
                  <span className="font-bold text-[#323232] font-gotham text-[15px] 2xl:text-[16px]">Y Axis:</span>
                  <span className="text-[#323232] font-normal font-gotham text-[15px] 2xl:text-[16px] ml-2">If you did this work, how high leverage would it be?</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Grid + axis labels (wider column) */}
          <div className="lg:col-span-2 relative flex flex-col">
            {/* Vertical Y axis label (left) */}
            <div className="absolute left-[-30px] top-36 transform -rotate-90 origin-left">
              <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">Very High Leverage</span>
            </div>

            {/* The 2x2 grid (2 cols) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Card 1 (top-left) */}
              <div className="rounded-[14px] bg-[#DBF3F3] border border-[#CCCCCC] p-4 2xl:p-8 min-h-[240px] 2xl:min-h-[280px] flex flex-col justify-center">
                <div className="text-[11px] 2xl:text-[12px] text-center uppercase tracking-wide font-gotham text-[#6CA3A3] font-medium mb-2">Your Approach</div>
                <div className="text-center text-lg lg:text-[14px] 2xl:text-[16.46px] font-gotham font-medium text-[#000000] leading-6">
                  Delegate to most suitable person and monitor actively & closely
                </div>
              </div>

              {/* Card 2 (top-right) */}
              <div className="rounded-[14px] bg-[#B0DFDE] border border-[#54B0AF] p-4 2xl:p-8 min-h-[240px] 2xl:min-h-[280px] flex flex-col justify-center">
                <div className="text-[11px] 2xl:text-[12px] text-center uppercase tracking-wide font-gotham text-[#6CA3A3] font-medium mb-2">Your Approach</div>
                <div className="text-center text-lg lg:text-[14px] 2xl:text-[16.46px] font-gotham font-medium text-[#000000] leading-6">
                  Focus deeply yourself and share learnings with your team after it's done
                </div>
              </div>

              {/* Card 3 (bottom-left) */}
              <div className="rounded-[14px] bg-[#F5FFFE] border border-[#54B0AF] p-4 2xl:p-8 min-h-[240px] 2xl:min-h-[280px] flex flex-col justify-center">
                <div className="text-[11px] 2xl:text-[12px] text-center uppercase tracking-wide font-gotham text-[#6CA3A3] font-medium mb-2">Your Approach</div>
                <div className="text-center text-lg lg:text-[14px] 2xl:text-[16.46px] font-gotham font-medium text-[#000000] leading-6">
                  Delegate and forget, but clarify that you are always available for help
                </div>
              </div>

              {/* Card 4 (bottom-right) */}
              <div className="rounded-[14px] bg-[#DBF3F3] border border-[#54B0AF] p-4 2xl:p-8 min-h-[240px] 2xl:min-h-[280px] flex flex-col justify-center">
                <div className="text-[11px] 2xl:text-[12px] text-center uppercase tracking-wide font-gotham text-[#6CA3A3] font-medium mb-2">Your Approach</div>
                <div className="text-center text-lg lg:text-[14px] 2xl:text-[16.46px] font-gotham font-medium text-[#000000] leading-6">
                  Set up the essential foundation and closely coach a team member as they do it
                </div>
              </div>
            </div>

            {/* Vertical Y axis bottom label (left) */}
            <div className="absolute left-[-36px] bottom-14 transform -rotate-90 origin-left">
              <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">Low Leverage</span>
            </div>

            {/* X axis labels */}
            <div className="flex justify-between mt-5 px-2">
              <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">Anyone can do</span>
              <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">Only you can do</span>
            </div>
          </div>
        </div>

        <div className="w-full flex items-center justify-between gap-4 mt-6 md:mt-10 lg:mt-6 lg:mt-22">
            <button
                onClick={onPreviousStep}
                className="flex items-center font-gotham font-medium gap-3 px-6 py-3 rounded-full bg-[#000000] text-white hover:bg-[#858585] transition-colors"
                aria-label="Previous Step"
            >
                <span className="bg-white rounded-full p-2">
                <ChevronRight className="w-4 h-4 rotate-180 text-black" />
                </span>
                Previous Step
            </button>

            <button
                onClick={onNextStep}
                className="inline-flex items-center gap-4 px-6 py-3 bg-black text-white rounded-full font-medium font-gotham text-lg hover:bg-neutral-800 transition-colors shadow"
                aria-label="Get Started"
            >
                Get Started
                <span className="bg-white rounded-full p-2">
                <ChevronRight className="w-4 h-4 text-black" />
                </span>
            </button>
            </div>
        </div>
    </div>
  );
};

export default DelegationFramework;

